<template>
  <div>
    <es-loader v-if="loading" style="margin: 6.5rem"/>
    <es-exception v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <es-form :padding="{ padding: '0 1.15rem' }">
        <template #es-form>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  Person Type
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <div class="buttons has-addons">
                      <button
                        v-for="(item, index) in sortedPersonTypesOptions"
                        :key="index"
                        class="button button--form"
                        @click="personType = item.code"
                        :class="{'is-active': personType === item.code}"
                        :disabled="!enabledSubmissionStatus.includes(item.code)"
                        style="min-width: 100px"
                      >
                        {{ item.name }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="hasModule('PRSID:MATCH:SUBMPURP:007MATCH') && !contextMode">
            <div class="row removeUnderline" >
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">
                    Submission Purpose
                  </label>
                </div>
                <div class="field-body">
                  <div class="buttons has-addons">
                    <button
                      v-for="(item, code) in submissionPurposes"
                      :key="code"
                      class="button button--form"
                      @click="submissionPurposeCode = item.code;"
                      :class="{'is-active': submissionPurposeCode === item.code}"
                      style="min-width: 100px"
                    >
                      {{ item.description }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row removeUnderline">
            <div class="field is-horizontal">
              <div class="field-label is-normal">
                <label class="label">
                  File to Upload
                </label>
              </div>
              <div class="field-body">
                <div class="field">
                  <div class="control">
                    <!-- adding flex classes for help text below file input -->
                    <div
                      class=
                      "is-flex
                        flex-direction--column
                        flex-justify--start
                        file
                        has-name
                        is-right
                        is-fullwidth"
                    >
                      <es-file-upload-input
                        :has-error-file="hasErrorFile"
                        input-id="id"
                        input-ref="inputFile"
                        :selected-files="files"
                        @validFileExtensions="setErrors($event)"
                      />
                      <div v-if="hasErrorFile" class="help is-danger">
                        {{ errorMsgFile }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </es-form>
      <pid-advanced-upload-format :formOptions="advancedFormOptions"/>
      <div
        class="is-flex flex-justify--end flex-align--center"
        style="margin-right: 5.15rem; padding-bottom: 2rem"
      >
        <button
          class="button button-link has-underline"
          @click="reset"
          style="margin-right: 1rem"
        >
          Reset
        </button>
        <button
          class="button is-primary"
          :disabled="loadingMatch || !isPersonTypeEnabled"
          @click="checkForm"
        >
          {{ properties['BUTTON_LABEL-ASSIGN_EXISTING'] }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapGetters } from 'vuex';
import EsFileUploadInput from '@/components/EsFileUploadInput.vue';
import EsForm from '@/components/EsForm.vue';
import EsException from '@/components/EsException.vue';
import EsLoader from '@/components/EsLoader.vue';
import PidAdvancedUploadFormat from '@/components/PidAdvancedUploadFormat.vue';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

export default {
  mixins: [
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsFileUploadInput,
    EsForm,
    EsException,
    EsLoader,
    PidAdvancedUploadFormat,
  },
  watch: {
    uploadType() {
      if (this.uploadType === 'SIMPLE') {
        this.setAdvancedFormOptions();
      }
    },
  },
  data() {
    return {
      allSubmissionSpecs: [],
      submissionPurposes: [],
      advancedFormOptions: {},
      errorMsgFile: null,
      files: [],
      hasErrorFile: false,
      loading: false,
      loadingError: null,
      loadingMatch: false,
      personType: null,
      userPersonTypesCodes: null,
      submissionPurposeCode: "001", //default to 001 for case when user does not have permission for other submission purposes
    };
  },
  created() {
    this.setAdvancedFormOptions();
  },
  mounted() {
    this.onLoad();
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('lookups', [
      'activeSources',
      'defaultPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    defaultSource() {
      if (this.activeSources) {
        const defaultType = this.activeSources
          .find(el => el.isDefault === true);
        return defaultType ? defaultType.code : this.activeSources[0].code;
      } return null;
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    isPersonTypeEnabled() {
      return this.enabledSubmissionStatus.includes(this.personType) && this.personType !== null;
    },
    enabledSubmissionStatus() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'ASSIGN' && d.enabled === true)
          .map(p => p.personType.code);
      } return null;
    },
    formDataBody() {
      const formData = new FormData();
      // TODO: currently the submissionTypeCode is hardcoded and while it will always be FILE
      //   we may want to load submission types here and use that
      formData.append('batchType', 'ASSIGN');
      formData.append('submissionTypeCode', 'FILE');
      formData.append('personTypeCode', this.personType);
      formData.append('submissionPurposeCode', this.submissionPurposeCode);
      formData.append('file', this.files[0]);
      formData.append('uploadType', this.advancedFormOptions.uploadType);
      if (this.advancedFormOptions.uploadType === 'ADVANCED') {
        formData.append('fileVersion', this.advancedFormOptions.fileFormat);
        formData.append('delimiter', this.advancedFormOptions.delimiter);
        formData.append('qualifier', this.advancedFormOptions.qualifier);
        formData.append('sourceCode', this.advancedFormOptions.sourceInput.length > 0
          ? this.advancedFormOptions.sourceInput[0].code : null);
        formData.append('ignoreFirstRow', this.advancedFormOptions.ignoreFirstRow);
      }
      return formData;
    },
    sortedPersonTypesOptions() {
      return this.userPersonTypes.slice().sort((a, b) => {
        if (a.name.toUpperCase() < b.name.toUpperCase()) {
          return -1;
        }
        if (a.name.toUpperCase() > b.name.toUpperCase()) {
          return 1;
        }
        return 0;
      });
    },
    uploadType() {
      return this.advancedFormOptions.uploadType;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
      'loadPersonTypes',
      'loadSubmissionPurposes',
    ]),
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        await Promise.all([
          this.loadSources(),
          this.loadPersonTypes(),
          this.loadSubmissionPurposes(),
        ]);
        this.userPersonTypesCodes = this.userPersonTypes
          .map(item => item.code);
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
        this.sourceInput = this.defaultSource;
        this.loadSubmissionSpecs();
      } catch (err) {
        this.loadingError = true;
        this.messages.push('There were some errors. Please try again. If errors persist please contact support.');
        this.showNotification('danger', this.messages);
      } finally {
        this.loading = false;
      }
    },
    async loadSubmissionSpecs() {
      const personTypeIds = this.userPersonTypes.map(item => item.id);
      try {
        this.loading = true;
        this.loadingError = false;
        // build promises to get submission specs info per person type
        const submissionSpecResponse = await Promise.all([...personTypeIds]
          .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
        submissionSpecResponse.forEach((s) => {
          this.allSubmissionSpecs.push(s.data);
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadSubmissionPurposes() {
      try {
        this.loading = true;
        this.loadingError = false;
        // call API to get submission purposes 
        const params = { category: 'MATCH'};
        const resp = await axios.get(`/submission-purposes/`, {params});
        this.submissionPurposes = resp.data;
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    checkForm() {
      if (this.files.length) {
        if (!this.hasErrorFile) {
          this.match();
        } else {
          this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        }
      } else {
        this.hasErrorFile = true;
        this.errorMsgFile = 'No file chosen. A File is required to Upload.';
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorFile = false;
      this.errorMsgFile = null;
    },
    async match() {
      try {
        this.clearErrors();
        this.loadingMatch = true; // local loading variable
        this.$root.$emit('matchOptionsLoading', true); // Match Results loader
        this.$root.$emit('matchOptionsLoadingError', false); // Match Results error message
        const response = await axios.post('/personid/batches', this.formDataBody);
        this.$emit('batchResponse', response.data); // emit batch data up to parent level
        this.$emit('newMatch');
      } catch (error) {
        this.showNotification('danger', [error.response.data.message]);
        this.$root.$emit('matchOptionsLoadingErrorMessage', error.response.data.message);
        this.$root.$emit('matchOptionsLoadingError', true); // Match Results error message
      } finally {
        this.loadingMatch = false; // local loading variable
        this.$root.$emit('matchOptionsLoading', false); // Match Results loader
      }
    },
    reset() {
      // reset the Simple form.
      // set the uploadType back to Simple to trigger the watcher.
      this.files = [];
      this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
        ? this.defaultPersonType : this.userPersonTypesCodes[0];
      this.advancedFormOptions.uploadType = 'SIMPLE';
      this.$root.$emit('matchOptionsLoadingError', false); // Match Results error message
      this.clearErrors();
    },
    setAdvancedFormOptions() {
      this.advancedFormOptions = {
        uploadType: 'SIMPLE',
        fileFormat: '3.0',
        delimiter: 'TAB',
        qualifier: 'NONE',
        sourceInput: [{ code: this.defaultSource }],
        ignoreFirstRow: false,
      };
    },
    setErrors(value) {
      if (!value) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'File extension is invalid. Valid extensions are \'.txt\' or \'.csv\' or \'.TXT\' or \'.CSV\'';
      } else {
        this.clearErrors();
      }
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
